import { useCallback, useEffect, useState } from "react";
import { checkIsIos } from "@/shared/pwa/banner/utils/check-is-ios";
import { PWA_ARTICLE_URL } from "@/shared/external-resourses";

const PWA_BANNER_DISMISSED_KEY = "pwa-banner-dismissed-at";
const PWA_INSTALLED_KEY = "pwa-installed";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type MyWindow = Window & { __beforeInstallPWAEvent?: BeforeInstallPromptEvent };

const isInstallationAvailable = !!(window as MyWindow).__beforeInstallPWAEvent;

export function usePwaBanner() {
  const [visible, setVisible] = useState(false);
  const isIos = checkIsIos();

  useEffect(() => {
    const dismissedAt = localStorage.getItem(PWA_BANNER_DISMISSED_KEY);
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)",
    ).matches;

    if (isStandalone) return;

    const isInstalled = localStorage.getItem(PWA_INSTALLED_KEY);
    if (isInstalled) return;

    if (!dismissedAt) {
      setVisible(true);
      return;
    }

    const elapsed = Date.now() - Number(dismissedAt);
    if (elapsed >= ONE_WEEK_MS) {
      localStorage.removeItem(PWA_BANNER_DISMISSED_KEY);
      setVisible(true);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    localStorage.setItem(PWA_BANNER_DISMISSED_KEY, String(Date.now()));
  }, []);

  const handleInstall = useCallback(async () => {
    const promptEvent = (window as MyWindow).__beforeInstallPWAEvent;

    if (promptEvent) {
      await promptEvent.prompt();
      const result = await promptEvent.userChoice;

      if (result.outcome === "accepted") {
        localStorage.setItem(PWA_INSTALLED_KEY, "true");
        setVisible(false);
      }
    }
  }, []);

  return {
    isIos,
    handleInstall,
    handleDismiss,
    visible,
    ARTICLE_URL: PWA_ARTICLE_URL,
    isInstallationAvailable,
  };
}
